<script setup lang="ts">
import { computed, ref } from 'vue';
import { toast } from 'vue-sonner';
import { useQueryClient } from '@tanstack/vue-query';
import { ArchiveBoxXMarkIcon, CheckCircleIcon, ListBulletIcon, PauseCircleIcon, PlayCircleIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import OverflowText from '@/components/OverflowText.vue';
import { useRawDdexParties, type RawDdexPartyOption } from '@/composables/api/useRawDdexParties';
import {
  DDEX_CONFIGURATIONS_QUERY_KEY,
  useCreateDdexConfiguration,
  useDdexConfigurations,
  useDeleteDdexConfiguration,
  useUpdateDdexConfiguration,
  type DdexConfiguration,
} from '@/composables/api/useDdexConfigurations';
import {
  canPurgeCatalogTracks,
  isCatalogPurgeComplete,
  isCatalogPurgePending,
  isCatalogPurgeStale,
  useCreateRawDdexPartyExclusion,
  usePurgeRawDdexPartyExclusionTracks,
  type RawDdexPartyExclusion,
} from '@/composables/api/useRawDdexPartyExclusions';

const queryClient = useQueryClient();

// Create-config form state.
const senderSelection = ref<string>('');
const importPathDraft = ref('');
const enabledDraft = ref(true);
const formError = ref<string | null>(null);

// Per-party (detail) view: the config whose exclusions we are managing inline.
const selectedConfigId = ref<number | null>(null);
const behalfSelection = ref<string>('');
const exclusionReasonDraft = ref('');
const exclusionFormError = ref<string | null>(null);

// Destructive-action confirmations.
const deleteConfirmConfig = ref<DdexConfiguration | null>(null);
const purgeConfirmExclusion = ref<RawDdexPartyExclusion | null>(null);
const purgingId = ref<number | null>(null);
// Which config's enable/disable PATCH is in flight, so only that row's toggle is disabled.
const togglingId = ref<number | null>(null);

const { data: configsResponse, isLoading: configsLoading } = useDdexConfigurations();
const { data: partiesResponse, isLoading: partiesLoading } = useRawDdexParties();

const configurations = computed<DdexConfiguration[]>(() => configsResponse.value ?? []);
const parties = computed<RawDdexPartyOption[]>(() => partiesResponse.value ?? []);
const sortedParties = computed(() =>
  [...parties.value].sort((a, b) => (a.name ?? a.party_id).localeCompare(b.name ?? b.party_id)),
);

// Create form: only senders without a configuration yet (one config per sender), so the picker
// can't offer a duplicate that would only fail at the API.
const configuredSenderIds = computed(
  () => new Set(configurations.value.map((configuration) => configuration.raw_ddex_party_sender_id)),
);
const availableSenders = computed(() =>
  sortedParties.value.filter((party) => !configuredSenderIds.value.has(party.id)),
);

const selectedConfig = computed<DdexConfiguration | null>(
  () => configurations.value.find((config) => config.id === selectedConfigId.value) ?? null,
);
const selectedConfigExclusions = computed<RawDdexPartyExclusion[]>(
  () => selectedConfig.value?.exclusions ?? [],
);

const createMutation = useCreateDdexConfiguration();
const updateMutation = useUpdateDdexConfiguration();
const deleteMutation = useDeleteDdexConfiguration();
const createExclusionMutation = useCreateRawDdexPartyExclusion();
const purgeMutation = usePurgeRawDdexPartyExclusionTracks();

function partyName(party: { name?: string | null; party_id?: string | null } | null): string {
  if (!party) return '—';
  return party.name ?? party.party_id ?? '—';
}

function exclusionStatusLabel(exclusion: RawDdexPartyExclusion): string {
  if (isCatalogPurgeComplete(exclusion)) return 'Catalog purged';
  if (isCatalogPurgePending(exclusion)) return 'Purging catalog…';
  if (isCatalogPurgeStale(exclusion)) return 'Purge stalled';
  return 'Blocks new ingestion';
}

function exclusionStatusDetail(exclusion: RawDdexPartyExclusion): string {
  if (exclusion.tracks_purged_at) {
    return `Completed ${new Date(exclusion.tracks_purged_at).toLocaleString()}`;
  }
  if (isCatalogPurgeStale(exclusion) && exclusion.tracks_purge_queued_at) {
    return `Queued ${new Date(exclusion.tracks_purge_queued_at).toLocaleString()} — taking longer than expected. Refresh the page; if the purge failed you can try again.`;
  }
  if (exclusion.tracks_purge_queued_at) {
    return `Queued ${new Date(exclusion.tracks_purge_queued_at).toLocaleString()}`;
  }
  return 'Existing catalog unchanged';
}

// "Added by" / "Added" lines — who created a row and when. Shared by configs and exclusions
// (both carry created_by_user + created_at).
function addedBy(entity: { created_by_user: { name: string | null; email: string | null } | null }): string {
  return entity.created_by_user?.name ?? entity.created_by_user?.email ?? '—';
}

function addedAt(entity: { created_at: string | null }): string {
  return entity.created_at ? new Date(entity.created_at).toLocaleString() : '—';
}

async function submitConfiguration() {
  formError.value = null;

  const senderId = senderSelection.value ? Number(senderSelection.value) : null;
  if (senderId === null) {
    formError.value = 'Sender is required.';
    return;
  }
  const importPath = importPathDraft.value.trim();
  if (!importPath) {
    formError.value = 'S3 import path is required.';
    return;
  }

  try {
    await createMutation.mutateAsync({
      raw_ddex_party_sender_id: senderId,
      s3_import_path: importPath,
      enabled: enabledDraft.value,
    });
    toast.success('Configuration added.');
    senderSelection.value = '';
    importPathDraft.value = '';
    enabledDraft.value = true;
  } catch (error: unknown) {
    const message = extractApiErrorMessage(error) ?? 'Failed to add configuration.';
    formError.value = message;
    toast.error(message);
  }
}

async function toggleEnabled(config: DdexConfiguration) {
  togglingId.value = config.id;
  try {
    await updateMutation.mutateAsync({ id: config.id, input: { enabled: !config.enabled } });
    toast.success(config.enabled ? 'Configuration disabled.' : 'Configuration enabled.');
  } catch (error: unknown) {
    toast.error(extractApiErrorMessage(error) ?? 'Failed to update configuration.');
  } finally {
    togglingId.value = null;
  }
}

async function confirmDelete() {
  const config = deleteConfirmConfig.value;
  if (!config) return;
  try {
    await deleteMutation.mutateAsync(config.id);
    toast.success('Configuration deleted.');
    if (selectedConfigId.value === config.id) selectedConfigId.value = null;
    deleteConfirmConfig.value = null;
  } catch (error: unknown) {
    toast.error(extractApiErrorMessage(error) ?? 'Failed to delete configuration.');
  }
}

function viewConfig(config: DdexConfiguration) {
  selectedConfigId.value = config.id;
  behalfSelection.value = '';
  exclusionReasonDraft.value = '';
  exclusionFormError.value = null;
}

async function submitExclusion() {
  exclusionFormError.value = null;
  const config = selectedConfig.value;
  if (!config) return;

  const behalfId = behalfSelection.value ? Number(behalfSelection.value) : null;
  if (behalfId === null) {
    exclusionFormError.value = 'Sent on behalf of is required.';
    return;
  }

  try {
    await createExclusionMutation.mutateAsync({
      ddex_configuration_id: config.id,
      raw_ddex_party_sent_on_behalf_of_id: behalfId,
      reason: exclusionReasonDraft.value.trim() ? exclusionReasonDraft.value.trim() : null,
    });
    // The exclusion mutation invalidates its own query; refresh configs too so the inline list updates.
    await queryClient.invalidateQueries({ queryKey: DDEX_CONFIGURATIONS_QUERY_KEY });
    toast.success('Exclusion added.');
    behalfSelection.value = '';
    exclusionReasonDraft.value = '';
  } catch (error: unknown) {
    const message = extractApiErrorMessage(error) ?? 'Failed to add exclusion.';
    exclusionFormError.value = message;
    toast.error(message);
  }
}

async function confirmPurgeTracks() {
  const exclusion = purgeConfirmExclusion.value;
  if (!exclusion) return;

  purgingId.value = exclusion.id;
  try {
    await purgeMutation.mutateAsync(exclusion.id);
    await queryClient.invalidateQueries({ queryKey: DDEX_CONFIGURATIONS_QUERY_KEY });
    toast.success('Catalog purge queued.');
    purgeConfirmExclusion.value = null;
  } catch (error: unknown) {
    toast.error(extractApiErrorMessage(error) ?? 'Failed to queue catalog purge.');
  } finally {
    purgingId.value = null;
  }
}

interface AxiosLikeError {
  response?: {
    data?: {
      message?: string;
      errors?: Record<string, string[]>;
    };
  };
}

function extractApiErrorMessage(error: unknown): string | null {
  const response = (error as AxiosLikeError | undefined)?.response?.data;
  if (!response) return null;
  if (response.errors) {
    const firstField = Object.keys(response.errors)[0];
    const firstMessage = firstField ? response.errors[firstField]?.[0] : undefined;
    if (firstMessage) return firstMessage;
  }
  return response.message ?? null;
}
</script>

<template>
  <div class="container mx-auto max-w-5xl space-y-6 p-6 max-md:pb-mobile-area-bar-clearance">
    <header>
      <h1 class="text-2xl font-semibold">DDEX</h1>
      <p class="mt-1 text-sm text-muted-foreground">
        One configuration per <strong>sender</strong> (distributor): its gated <strong>S3 import
        path</strong> and an <strong>enabled</strong> toggle that drives the hourly ingest pipeline.
        Disabling a sender stops fetch, parse, and match for it; re-enabling resumes and drains the
        backlog. Open a configuration to manage that sender's exclusions.
      </p>
    </header>

    <Card>
      <CardHeader>
        <CardTitle>Add a configuration</CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">
        <form class="flex flex-wrap items-end gap-3" @submit.prevent="submitConfiguration">
          <div class="min-w-[220px] flex-1 space-y-1">
            <label for="config-sender" class="text-sm font-medium">
              Sender <span class="text-destructive">*</span>
            </label>
            <Select v-model="senderSelection" :disabled="partiesLoading">
              <SelectTrigger id="config-sender" class="w-full">
                <SelectValue placeholder="Pick a sender…" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem
                    v-for="party in availableSenders"
                    :key="`config-sender-${party.id}`"
                    :value="String(party.id)"
                  >
                    {{ party.name ?? party.party_id }}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div class="min-w-[220px] flex-1 space-y-1">
            <label for="config-path" class="text-sm font-medium">
              S3 import path <span class="text-destructive">*</span>
            </label>
            <Input
              id="config-path"
              v-model="importPathDraft"
              placeholder="e.g. fuga/ready"
            />
          </div>

          <label class="flex items-center gap-2 text-sm font-medium">
            <input v-model="enabledDraft" type="checkbox" class="h-4 w-4" />
            Enabled
          </label>

          <Button type="submit" :disabled="createMutation.isPending.value">
            {{ createMutation.isPending.value ? 'Adding…' : 'Add configuration' }}
          </Button>
        </form>

        <p class="text-xs text-muted-foreground">
          Use the gated delivery sub-path (e.g. <span class="font-mono">fuga/ready</span>,
          <span class="font-mono">sme/new_releases/ready</span>), not the bare sender folder —
          otherwise mid-upload or superseded deliveries get pulled in.
        </p>

        <p v-if="formError" class="text-sm text-destructive">{{ formError }}</p>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>Configurations</CardTitle>
      </CardHeader>
      <CardContent>
        <div v-if="configsLoading" class="py-6 text-center text-sm text-muted-foreground">
          Loading…
        </div>
        <div
          v-else-if="configurations.length === 0"
          class="py-6 text-center text-sm text-muted-foreground"
        >
          No configurations yet. Add one above to start ingesting from a sender.
        </div>
        <Table v-else class="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead class="w-[20%]">Sender</TableHead>
              <TableHead class="w-[22%]">S3 import path</TableHead>
              <TableHead class="w-[10%]">Status</TableHead>
              <TableHead class="w-[9%]">Exclusions</TableHead>
              <TableHead class="w-[19%]">Added</TableHead>
              <TableHead class="w-[20%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow
              v-for="config in configurations"
              :key="config.id"
              :class="['cursor-pointer hover:bg-muted/30', config.id === selectedConfigId ? 'bg-muted/40' : '']"
              @click="viewConfig(config)"
            >
              <TableCell>
                <OverflowText :value="partyName(config.sender)" />
              </TableCell>
              <TableCell class="font-mono">
                <OverflowText :value="config.s3_import_path" />
              </TableCell>
              <TableCell>
                <span
                  :class="[
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    config.enabled
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                      : 'bg-muted text-muted-foreground',
                  ]"
                >
                  {{ config.enabled ? 'Active' : 'Inactive' }}
                </span>
              </TableCell>
              <TableCell class="text-muted-foreground">
                {{ config.exclusions?.length ?? 0 }}
              </TableCell>
              <TableCell class="text-muted-foreground">
                <OverflowText :value="addedBy(config)" />
                <p class="text-xs text-muted-foreground">{{ addedAt(config) }}</p>
              </TableCell>
              <!-- Row click opens the exclusions list; stop here so action buttons don't also select. -->
              <TableCell class="text-right" @click.stop>
                <div class="flex justify-end gap-1">
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <Button
                        variant="ghost"
                        size="icon"
                        :disabled="togglingId === config.id"
                        :aria-label="config.enabled ? 'Deactivate configuration' : 'Activate configuration'"
                        @click="toggleEnabled(config)"
                      >
                        <PauseCircleIcon v-if="config.enabled" class="h-5 w-5 text-green-600" />
                        <PlayCircleIcon v-else class="h-5 w-5 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {{ config.enabled
                          ? 'Deactivate — stops fetch, parse, and match for this sender'
                          : 'Activate — resumes the hourly pipeline' }}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="View exclusions"
                        @click="viewConfig(config)"
                      >
                        <ListBulletIcon class="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View this sender's exclusions</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete this configuration"
                        @click="deleteConfirmConfig = config"
                      >
                        <TrashIcon class="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Delete configuration (does not delete exclusions or catalog).</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    <!-- Per-party (detail) view: manage the selected sender's exclusions. -->
    <Card v-if="selectedConfig">
      <CardHeader>
        <CardTitle>
          Exclusions for {{ partyName(selectedConfig.sender) }}
          <span class="ml-2 font-mono text-sm text-muted-foreground">{{ selectedConfig.s3_import_path }}</span>
        </CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">
        <form class="flex flex-wrap items-end gap-3" @submit.prevent="submitExclusion">
          <div class="min-w-[220px] flex-1 space-y-1">
            <label for="exclusion-behalf" class="text-sm font-medium">
              Sent on behalf of <span class="text-destructive">*</span>
            </label>
            <Select v-model="behalfSelection" :disabled="partiesLoading">
              <SelectTrigger id="exclusion-behalf" class="w-full">
                <SelectValue placeholder="Pick sent on behalf of…" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem
                    v-for="party in sortedParties"
                    :key="`behalf-${party.id}`"
                    :value="String(party.id)"
                  >
                    {{ party.name ?? party.party_id }}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div class="min-w-[220px] flex-1 space-y-1">
            <label for="exclusion-reason" class="text-sm font-medium">Reason (optional)</label>
            <Input id="exclusion-reason" v-model="exclusionReasonDraft" placeholder="Why exclude this pair?" />
          </div>

          <Button type="submit" :disabled="createExclusionMutation.isPending.value">
            {{ createExclusionMutation.isPending.value ? 'Adding…' : 'Add exclusion' }}
          </Button>
        </form>
        <p v-if="exclusionFormError" class="text-sm text-destructive">{{ exclusionFormError }}</p>

        <div
          v-if="selectedConfigExclusions.length === 0"
          class="py-4 text-center text-sm text-muted-foreground"
        >
          No exclusions for this sender.
        </div>
        <Table v-else class="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead class="w-[24%]">Sent on behalf of</TableHead>
              <TableHead class="w-[26%]">Status</TableHead>
              <TableHead class="w-[18%]">Reason</TableHead>
              <TableHead class="w-[22%]">Added</TableHead>
              <TableHead class="w-[10%] text-right">Purge</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="exclusion in selectedConfigExclusions" :key="exclusion.id">
              <TableCell>
                <OverflowText :value="partyName(exclusion.sent_on_behalf_of)" />
              </TableCell>
              <TableCell class="text-muted-foreground">
                <div class="flex items-start gap-2">
                  <CheckCircleIcon
                    v-if="isCatalogPurgeComplete(exclusion)"
                    class="mt-0.5 h-4 w-4 shrink-0 text-green-600"
                    aria-hidden="true"
                  />
                  <div class="min-w-0">
                    <OverflowText :value="exclusionStatusLabel(exclusion)" />
                    <p class="text-xs text-muted-foreground">{{ exclusionStatusDetail(exclusion) }}</p>
                  </div>
                </div>
              </TableCell>
              <TableCell class="text-muted-foreground">
                <OverflowText :value="exclusion.reason ?? '—'" />
              </TableCell>
              <TableCell class="text-muted-foreground">
                <OverflowText :value="addedBy(exclusion)" />
                <p class="text-xs text-muted-foreground">{{ addedAt(exclusion) }}</p>
              </TableCell>
              <TableCell class="text-right">
                <div class="flex justify-end">
                  <Tooltip v-if="canPurgeCatalogTracks(exclusion)">
                    <TooltipTrigger as-child>
                      <Button
                        variant="ghost"
                        size="icon"
                        :disabled="purgingId === exclusion.id"
                        aria-label="Purge existing catalog for this exclusion pair"
                        @click="purgeConfirmExclusion = exclusion"
                      >
                        <ArchiveBoxXMarkIcon class="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent class="max-w-sm">
                      <p>
                        Purge existing catalog for this pair: soft-delete matching raw DDEX tracks,
                        remove matches, and unlink live tracks. This cannot be undone.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    <Dialog
      :open="deleteConfirmConfig !== null"
      @update:open="(open) => { if (!open) deleteConfirmConfig = null; }"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this configuration?</DialogTitle>
          <DialogDescription>
            The sender stops being scheduled for ingest. Exclusion rules and already-ingested catalog
            are left untouched.
          </DialogDescription>
        </DialogHeader>
        <p v-if="deleteConfirmConfig" class="font-mono text-sm">
          {{ partyName(deleteConfirmConfig.sender) }} — {{ deleteConfirmConfig.s3_import_path }}
        </p>
        <DialogFooter>
          <DialogClose as-child>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            :disabled="deleteMutation.isPending.value"
            @click="confirmDelete"
          >
            {{ deleteMutation.isPending.value ? 'Deleting…' : 'Delete' }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog
      :open="purgeConfirmExclusion !== null"
      @update:open="(open) => { if (!open) purgeConfirmExclusion = null; }"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Purge existing catalog for this pair?</DialogTitle>
          <DialogDescription>
            Queues a one-way cleanup: soft-deletes matching raw DDEX tracks, removes live track and
            fingerprint matches, and unlinks linked live tracks. The exclusion rule stays in place.
          </DialogDescription>
        </DialogHeader>
        <p class="text-destructive font-medium">This cannot be undone.</p>
        <DialogFooter>
          <DialogClose as-child>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button variant="destructive" :disabled="purgingId !== null" @click="confirmPurgeTracks">
            {{ purgingId !== null ? 'Queuing…' : 'Purge catalog' }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
